import { AlarmModel } from '../model/AlarmModel';
import { BadgeListInfoPaginated, BadgeStudioFilterBy, PageSizeTypeExtended } from '../model/BadgeModel';
import { OperatorsDetail, PaginationSetting, ResponseObject, ResponseObjectEntity, TupleTyped } from '../model/CommonModel';
import { ObjectTypes, PortStyle, VelocityDevice } from '../model/DeviceAdminModel';
import {
	AlarmQueueDevice,
	BadgeDirectoryInfo,
	BadgeDirectoryInfoPagination,
	BadgeStudioSpaceObjects,
	ContextMenuRequest,
	DeviceAlarmStatus,
	GetGraphicDirectoryByRequest,
	GraphicDirectoryInfo,
	GraphicDirectoryInfoPagination,
	GraphicType,
	IDSAreaBasicData,
	ImagePlaceHolder,
	ObjectLibraryTreeNode,
	ObjectSearchField,
	PendingItem,
	SaveBadgeStudioDTO,
	SpaceObjectSerialize,
	UpdateTreeLevelNode,
	VOType,
} from '../model/GraphicModel';
import { UserDefinedFieldSetup } from '../model/UserDefinedFields';
import { Api } from './api';

class GraphicApi extends Api {
	public constructor() {
		super();
	}

	public getConvertedMaps = async (canvasId: number): Promise<ResponseObjectEntity<GraphicDirectoryInfoPagination>> => {
		const url = this.getUrl('Graphic/GetConvertedMaps', true);
		const data = {
			canvasId,
		};

		return await this.post<ResponseObjectEntity<GraphicDirectoryInfoPagination>>(url, data);
	};

	public getConvertedBadges = async (canvasId: number): Promise<ResponseObjectEntity<BadgeDirectoryInfoPagination>> => {
		const url = this.getUrl('Graphic/GetConvertedBadges', true);
		const data = {
			canvasId,
		};

		return await this.post<ResponseObjectEntity<BadgeDirectoryInfoPagination>>(url, data);
	};

	public getMapDirectoryBy = async (request: GetGraphicDirectoryByRequest): Promise<ResponseObjectEntity<GraphicDirectoryInfoPagination>> => {
		const url = this.getUrl('Graphic/GetMapDirectoryBy', true);
		const data = {
			request,
		};

		return await this.post<ResponseObjectEntity<GraphicDirectoryInfoPagination>>(url, data);
	};

	public getBadgeDirectoryBy = async (paginationSetting: PaginationSetting): Promise<ResponseObjectEntity<BadgeDirectoryInfoPagination>> => {
		const url = this.getUrl('Graphic/GetBadgeDirectoryBy', true);
		const data = {
			paginationSetting,
		};

		return await this.post<ResponseObjectEntity<BadgeDirectoryInfoPagination>>(url, data);
	};

	public getMapDirectoryByMapType = async (mapType: GraphicType): Promise<ResponseObjectEntity<number[]>> => {
		const url = this.getUrl('Graphic/GetMapDirectoryByMapType', true);
		const data = {
			mapType,
		};

		return await this.post<ResponseObjectEntity<number[]>>(url, data);
	};

	public getMap = async (mapInfo: Partial<GraphicDirectoryInfo>, overrideExistingMapId: number = 0): Promise<ResponseObjectEntity<SpaceObjectSerialize>> => {
		const url = this.getUrl('Graphic/GetMap', true);
		const data = { mapInfo, overrideExistingMapId };

		return await this.post<ResponseObjectEntity<SpaceObjectSerialize>>(url, data);
	};

	public checkDuplicatedName = async (name: string, isMap: boolean = true): Promise<ResponseObjectEntity<boolean>> => {
		const url = this.getUrl('Graphic/CheckDuplicatedName', true);
		const data = { name, isMap };

		return await this.post<ResponseObjectEntity<boolean>>(url, data);
	};

	public createNewMap = async (mapName: string): Promise<ResponseObjectEntity<SpaceObjectSerialize>> => {
		const url = this.getUrl('Graphic/CreateNewMap', true);
		const data = { mapName };

		return await this.post<ResponseObjectEntity<SpaceObjectSerialize>>(url, data);
	};

	public renameLegacyMap = async (canvasId: number, name: string): Promise<ResponseObject> => {
		const url = this.getUrl('Graphic/RenameLegacyMap', true);
		const data = { canvasId, name };

		return await this.post<ResponseObject>(url, data);
	};

	public saveGraphicGroup = async (graphicGroup: UpdateTreeLevelNode): Promise<ResponseObjectEntity<boolean>> => {
		const url = this.getUrl('Graphic/SaveGraphicGroup', true);
		const data = { graphicGroup };

		return await this.post<ResponseObjectEntity<boolean>>(url, data);
	};

	public saveGraphicGroupAfterDeletion = async (updateTreeLevelNode: UpdateTreeLevelNode): Promise<ResponseObject> => {
		const url = this.getUrl('Graphic/SaveGraphicGroupAfterDeletion', true);
		const data = { updateTreeLevelNode };

		return await this.post<ResponseObject>(url, data);
	};

	public deleteGraphic = async (graphicId: number): Promise<ResponseObject> => {
		const url = this.getUrl('Graphic/DeleteGraphic', true);
		const data = { graphicId };

		return await this.post<ResponseObject>(url, data);
	};

	public deleteMap = async (mapId: number, mapType: GraphicType): Promise<ResponseObject> => {
		const url = this.getUrl('Graphic/DeleteMap', true);
		const data = { mapId, mapType };

		return await this.post<ResponseObject>(url, data);
	};

	public executeShowCredInfo = async (request: ContextMenuRequest): Promise<ResponseObject> => {
		const url = this.getUrl('Graphic/ExecuteShowCredInfo', true);
		return await this.post<ResponseObjectEntity<boolean>>(url, request);
	};

	public getObjectLibrary = async (): Promise<ResponseObjectEntity<ObjectLibraryTreeNode>> => {
		const url = this.getUrl('Graphic/GetObjectLibrary', true);

		return await this.post<ResponseObjectEntity<ObjectLibraryTreeNode>>(url);
	};

	public getObjectsLibraryExpanded = async (
		parentId: number,
		objectType: ObjectTypes,
		portStyle: PortStyle
	): Promise<ResponseObjectEntity<ObjectLibraryTreeNode[]>> => {
		const url = this.getUrl('Graphic/GetObjectsLibraryExpanded', true);
		const data = {
			parentId,
			objectType,
			portStyle,
		};

		return await this.post<ResponseObjectEntity<ObjectLibraryTreeNode[]>>(url, data);
	};

	public getErrorMessages = async (): Promise<ResponseObjectEntity<TupleTyped<number, string>[]>> => {
		const url = this.getUrl('Graphic/GetErrorMessages', true);

		return await this.post<ResponseObjectEntity<TupleTyped<number, string>[]>>(url);
	};

	public searchObjectLibrary = async (name: string, targets: ObjectSearchField[]): Promise<ResponseObjectEntity<ObjectLibraryTreeNode[]>> => {
		const url = this.getUrl('Graphic/SearchObjectLibrary', true);
		const data = { name, targets };

		return await this.post<ResponseObjectEntity<ObjectLibraryTreeNode[]>>(url, data);
	};

	public getBlankDrawing = async (): Promise<ResponseObjectEntity<string>> => {
		const url = this.getUrl('Graphic/GetBlankDrawing', true);

		return await this.post<ResponseObjectEntity<string>>(url, {});
	};

	public importMapDrawing = async (formData: FormData): Promise<ResponseObjectEntity<SpaceObjectSerialize>> => {
		const url = this.getUrl('Graphic/ImportMapDrawing', true);

		return await this.post<ResponseObjectEntity<SpaceObjectSerialize>>(url, formData);
	};

	public importBadgeDrawing = async (formData: FormData): Promise<ResponseObjectEntity<BadgeStudioSpaceObjects>> => {
		const url = this.getUrl('Graphic/ImportBadgeDrawing', true);

		return await this.post<ResponseObjectEntity<BadgeStudioSpaceObjects>>(url, formData);
	};

	public getMapBasicData = async (mapId: number, mapType: GraphicType): Promise<ResponseObjectEntity<GraphicDirectoryInfo>> => {
		const url = this.getUrl('Graphic/GetMapBasicData', true);

		const data = {
			mapId,
			mapType,
		};

		return await this.post<ResponseObjectEntity<GraphicDirectoryInfo>>(url, data);
	};

	public getMapSelectedPage = async (mapId: number, mapType: GraphicType, pagination: PaginationSetting): Promise<ResponseObjectEntity<number>> => {
		const url = this.getUrl('Graphic/GetMapSelectedPage', true);

		const data = {
			mapId,
			mapType,
			pagination,
		};

		return await this.post<ResponseObjectEntity<number>>(url, data);
	};

	public getAlarmQueueDevices = async (mapIds: number[]): Promise<ResponseObjectEntity<AlarmQueueDevice[]>> => {
		const url = this.getUrl('Graphic/GetAlarmQueueDevices', true);

		return await this.post<ResponseObjectEntity<AlarmQueueDevice[]>>(url, { mapIds });
	};

	public getAlarmQueueDeviceAlarms = async (deviceInfo: PendingItem): Promise<ResponseObjectEntity<AlarmModel[]>> => {
		const url = this.getUrl('Graphic/GetAlarmQueueDeviceAlarms', true);

		return await this.post<ResponseObjectEntity<AlarmModel[]>>(url, { deviceInfo });
	};

	public getAlarmQueueAlarmKeys = async (devicesInfo: PendingItem[], isAcknowledge: boolean): Promise<ResponseObjectEntity<string[]>> => {
		const url = this.getUrl('Graphic/GetAlarmQueueAlarmKeys', true);

		return await this.post<ResponseObjectEntity<string[]>>(url, { devicesInfo, isAcknowledge });
	};

	public getAllAlarmQueueAlarmModels = async (mapIds: number[], isAcknowledge: boolean): Promise<ResponseObjectEntity<AlarmModel[]>> => {
		const url = this.getUrl('Graphic/GetAllAlarmQueueAlarmModels', true);

		return await this.post<ResponseObjectEntity<AlarmModel[]>>(url, { mapIds, isAcknowledge });
	};

	public getAlarmQueueDeviceAlarmModels = async (devicesInfo: PendingItem[], isAcknowledge: boolean): Promise<ResponseObjectEntity<AlarmModel[]>> => {
		const url = this.getUrl('Graphic/GetAlarmQueueDeviceAlarmModels', true);

		return await this.post<ResponseObjectEntity<AlarmModel[]>>(url, { devicesInfo, isAcknowledge });
	};

	public getDeviceAlarmStatus = async (address: string, objectType: ObjectTypes): Promise<ResponseObjectEntity<DeviceAlarmStatus>> => {
		const url = this.getUrl('Graphic/GetDeviceAlarmStatus', true);

		return await this.post<ResponseObjectEntity<DeviceAlarmStatus>>(url, { address, objectType });
	};

	public getOperatorsDetails = async (): Promise<ResponseObjectEntity<OperatorsDetail[]>> => {
		const url = this.getUrl('Graphic/GetOperatorsDetails', true);

		return await this.post<ResponseObjectEntity<OperatorsDetail[]>>(url);
	};

	public getBadgeList = async (pagination: PaginationSetting, filterByData: BadgeStudioFilterBy): Promise<ResponseObjectEntity<BadgeListInfoPaginated>> => {
		const url = this.getUrl('Graphic/GetBadgeList', true);

		return await this.post<ResponseObjectEntity<BadgeListInfoPaginated>>(url, { pagination, filterByData });
	};

	public archiveBadge = async (badgeId: number): Promise<ResponseObject> => {
		const url = this.getUrl('Graphic/ArchiveBadge', true);

		return await this.post<ResponseObject>(url, { badgeId });
	};

	public deleteBadge = async (badgeId: number): Promise<ResponseObject> => {
		const url = this.getUrl('Graphic/DeleteBadge', true);

		return await this.post<ResponseObject>(url, { badgeId });
	};

	public restoreBadges = async (badgeIds: number[]): Promise<ResponseObject> => {
		const url = this.getUrl('Graphic/RestoreBadges', true);
		return await this.post<ResponseObject>(url, { badgeIds });
	};

	public getArchivedBadgeIds = async (): Promise<ResponseObjectEntity<number[]>> => {
		const url = this.getUrl('Graphic/GetArchivedBadgeIds', true);
		return await this.post<ResponseObjectEntity<number[]>>(url);
	};

	public getCustomBadgeFields = async (): Promise<ResponseObjectEntity<string[]>> => {
		const url = this.getUrl('Graphic/GetCustomBadgeFields', true);

		return await this.post<ResponseObjectEntity<string[]>>(url);
	};

	public getAllMaps = async (): Promise<ResponseObjectEntity<GraphicDirectoryInfo[]>> => {
		const url = this.getUrl('Graphic/GetAllMaps', true);

		return await this.post<ResponseObjectEntity<GraphicDirectoryInfo[]>>(url);
	};

	public getImagePlaceHolders = async (): Promise<ResponseObjectEntity<ImagePlaceHolder[]>> => {
		const url = this.getUrl('Graphic/GetImagePlaceHolders', true);

		return await this.post<ResponseObjectEntity<ImagePlaceHolder[]>>(url);
	};

	public getAllControllers = async (): Promise<ResponseObjectEntity<VelocityDevice[]>> => {
		const url = this.getUrl('Graphic/GetAllControllers', true);

		return await this.post<ResponseObjectEntity<VelocityDevice[]>>(url);
	};

	public getControllerControlZonesByControllerId = async (controllerId: number): Promise<ResponseObjectEntity<VelocityDevice[]>> => {
		const url = this.getUrl('Graphic/GetControllerControlZonesByControllerId', true);

		return await this.post<ResponseObjectEntity<VelocityDevice[]>>(url, { controllerId });
	};

	public getIDSAreas = async (): Promise<ResponseObjectEntity<IDSAreaBasicData[]>> => {
		const url = this.getUrl('Graphic/GeIDSAreas', true);

		return await this.post<ResponseObjectEntity<IDSAreaBasicData[]>>(url);
	};

	public getUdfFields = async (): Promise<ResponseObjectEntity<UserDefinedFieldSetup[]>> => {
		const url = this.getUrl('Graphic/GetUdfFields', true);

		return await this.post<ResponseObjectEntity<UserDefinedFieldSetup[]>>(url);
	};

	public getVoTypes = async (): Promise<ResponseObjectEntity<VOType[]>> => {
		const url = this.getUrl('Graphic/GetVoTypes', true);

		return await this.post<ResponseObjectEntity<VOType[]>>(url);
	};

	public editBadgeStudio = async (badge: SaveBadgeStudioDTO): Promise<ResponseObject> => {
		const url = this.getUrl('Graphic/EditBadge', true);

		return await this.post<ResponseObject>(url, { badge });
	};

	public addNewBadge = async (badgeName: string, preset: PageSizeTypeExtended): Promise<ResponseObjectEntity<BadgeStudioSpaceObjects>> => {
		const url = this.getUrl('Graphic/AddNewBadge', true);
		const data = { badgeName, preset };

		return await this.post<ResponseObjectEntity<BadgeStudioSpaceObjects>>(url, data);
	};

	public getBadge = async (
		badge: Partial<BadgeDirectoryInfo>,
		overrideExistingBadgeId: number = 0
	): Promise<ResponseObjectEntity<BadgeStudioSpaceObjects>> => {
		const url = this.getUrl('Graphic/GetBadge', true);

		return await this.post<ResponseObjectEntity<BadgeStudioSpaceObjects>>(url, { badge, overrideExistingBadgeId });
	};

	public getFontList = async (): Promise<ResponseObjectEntity<string[]>> => {
		const url = this.getUrl('Graphic/GetFontList', true);

		return await this.post<ResponseObjectEntity<string[]>>(url);
	};
}

const graphicApi = new GraphicApi();

export { graphicApi };
